import React from "react"
import { FaInstagram, FaTwitter, FaTiktok } from "react-icons/fa"
import { Analytics } from "@vercel/analytics/react"
import GradientBackground from './sections/GradientBackground'
import { Menu } from "lucide-react"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import PartnersSection from './sections/PartnersSection'
import SEO from './common/SEO'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({ children, className = "", ...props }) => {
  return (
    <button
      className={`inline-flex items-center justify-center text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}

interface FeatureCardProps {
  mockupImage: string;
  title: string;
  description: string;
}

interface SocialIconProps {
  Icon: React.ComponentType<{ className: string }>;
  href: string;
  label: string;
}

export const Header: React.FC = () => {
  return (
    <header className="fixed top-4 left-4 right-4 rounded-full border bg-white/80 backdrop-blur-sm z-50 shadow-lg">
      <div className="container flex h-14 items-center justify-between px-4">
        <a href="/" className="flex items-center space-x-2">
          <img src="/currents-logo.png" alt="Currents Logo" className="w-8 h-8" />
        </a>
        
        {/* Desktop Navigation */}
        <nav className="hidden items-center space-x-6 text-sm font-medium md:flex">
          <a href="/" className="text-foreground/60 transition hover:text-foreground">
            Home
          </a>
          <a href="/pro" className="text-foreground/60 transition hover:text-foreground flex items-center">
            Currents <span className="ml-1 bg-[#1F80FF]/10 text-[#1F80FF] px-2 py-0.5 rounded-lg text-xs font-semibold">PRO</span>
          </a>
          <a href="/research" className="text-foreground/60 transition hover:text-foreground">
            Research
          </a>
          <a href="/about" className="text-foreground/60 transition hover:text-foreground">
            About
          </a>
          <a href="/blog" className="text-foreground/60 transition hover:text-foreground">
            Blog
          </a>
        </nav>

        {/* Mobile Navigation */}
        <div className="flex items-center md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button className="p-2 hover:bg-transparent rounded-full">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </button>
            </SheetTrigger>
            <SheetContent side="top" className="h-screen pt-16">
              <nav className="flex flex-col space-y-8 items-center text-center">
                <a href="/" className="text-2xl font-medium hover:text-[#1F80FF]">
                  Home
                </a>
                <a href="/pro" className="text-2xl font-medium hover:text-[#1F80FF] flex items-center">
                  Currents <span className="ml-1 bg-[#1F80FF]/10 text-[#1F80FF] px-2 py-0.5 rounded-lg text-xs font-semibold">PRO</span>
                </a>
                <a href="/research" className="text-2xl font-medium hover:text-[#1F80FF]">
                  Research
                </a>
                <a href="/about" className="text-2xl font-medium hover:text-[#1F80FF]">
                  About
                </a>
                <a href="/blog" className="text-2xl font-medium hover:text-[#1F80FF]">
                  Blog
                </a>
                <div className="h-px w-24 bg-gray-200" />
                <a href="/terms-of-service" className="text-xl text-muted-foreground hover:text-[#1F80FF]">
                  Terms of Service
                </a>
                <a href="/privacy-policy" className="text-xl text-muted-foreground hover:text-[#1F80FF]">
                  Privacy Policy
                </a>
                <div className="w-full max-w-[300px] pt-4">
                  <Button 
                    className="rounded-full bg-zinc-900 text-white px-8 py-3 w-full"
                    onClick={() => window.open('https://apps.apple.com/us/app/diverplus-diving-app/id6504115639', '_blank')}
                  >
                    Download iOS App
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>

        {/* Desktop Download Button */}
        <div className="hidden md:block">
          <Button 
            className="rounded-full bg-zinc-900 text-white px-8 py-3"
            onClick={() => window.open('https://apps.apple.com/us/app/diverplus-diving-app/id6504115639', '_blank')}
          >
            Download iOS App
          </Button>
        </div>
      </div>
    </header>
  )
}

const HeroSection: React.FC = () => {
  return (
    <section className="container flex flex-col items-center justify-center space-y-4 pt-32 px-4 text-center">
      <img 
        src="/currents-logo.png" 
        alt="Currents Logo" 
        className="w-16 h-16 md:w-20 md:h-20 rounded-xl shadow-lg"
      />
      <h1 className="text-3xl md:text-4xl font-bold tracking-tighter sm:text-5xl">
        Dive More, Log Less
      </h1>
      <p className="max-w-[600px] text-xl md:text-2xl text-muted-foreground">
        Sync any dive computer in seconds - Scubapro, Suunto, Shearwater & 12+ more.
        Your certifications, gear history, and dive logs - finally in one place.
      </p>
      <Button 
        className="mt-4 rounded-full bg-zinc-900 px-8 py-3 text-white hover:bg-zinc-800"
        onClick={() => window.open('https://apps.apple.com/us/app/diverplus-diving-app/id6504115639', '_blank')}
      >
        Download iOS App
      </Button>
      <p className="text-sm text-muted-foreground">
        "Finally replaced my 3 dive apps" - Carlos, Dive Instructor
      </p>
    </section>
  )
}

const AppShowcase: React.FC = () => {
  return (
    <section className="container pb-8 px-4">
      <div className="flex justify-center items-center">
        <img
          src="/currents-show-mockup.png"
          alt="Currents App Mockup"
          className="rounded-2xl w-full max-w-[800px] h-auto"
        />
      </div>
    </section>
  )
}

const FeatureCard: React.FC<FeatureCardProps> = ({ mockupImage, title, description }) => {
  return (
    <div className="flex flex-col items-center relative z-20">
      <div className="w-full bg-white border rounded-2xl shadow-lg overflow-hidden">
        <div className="h-[200px] sm:h-[250px] md:h-[300px] lg:h-[380px] w-full p-4 flex items-center justify-center">
          <img
            src={mockupImage}
            alt={`${title} Feature`}
            className="h-full w-auto object-contain"
            style={{ maxWidth: '100%' }}
          />
        </div>
        <div className="p-6 flex flex-col items-center text-center">
          <h3 className="text-lg md:text-xl font-semibold mb-3">{title}</h3>
          <p className="text-sm md:text-base text-muted-foreground">
            {description}
          </p>
        </div>
      </div>
    </div>
  )
}

const FeaturesSection: React.FC = () => {
  const features: FeatureCardProps[] = [
    {
      mockupImage: "/currents-logbook-mockup.png",
      title: "One Logbook, All Your Dives",
      description: "Sync 15+ dive computers (Shearwater, Scubapro, Suunto, and more) via Bluetooth, or log dives manually in seconds. Keep certifications, gear service dates, and dive history organized – no more scattered notes."
    },
    {
      mockupImage: "/currents-home-mockup.png",
      title: "Your Water Tribe",
      description: "Swap safety tips for cenotes, find buddies for next month's trip, or argue about the best post-dive tacos in Roatán – all while your gear air-dries."
    },
    {
      mockupImage: "/currents-explore-mockup.png",
      title: "Plan Like a Local",
      description: "Pin your dives, filter by conditions ('Show me shore dives under 18m with octopus sightings'), or steal secret spots from instructors' logs."
    }
  ]

  return (
    <section className="container max-w-[1600px] pt-8 pb-20 px-4">
      <div className="text-center">
        <div className="inline-block rounded-full bg-[#1F80FF]/10 px-3 py-1 text-sm text-[#1F80FF]">
          Features
        </div>
        <h2 className="mt-4 text-2xl md:text-3xl font-bold sm:text-4xl">
          Everything You Need Between Dives
        </h2>
        <p className="mt-4 text-base md:text-lg text-muted-foreground">
          Sync multiple computers, organize certifications,
          and connect with divers who share your passion.
        </p>
      </div>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8 justify-items-center">
        {features.map((feature, index) => (
          <FeatureCard key={index} {...feature} />
        ))}
      </div>
    </section>
  )
}

const CtaSection: React.FC = () => {
  return (
    <section className="relative w-full py-16 md:py-24">
      <div className="container relative z-10 flex flex-col items-center space-y-4 text-center px-4">
        <img 
          src="/currents-logo.png" 
          alt="Currents Logo" 
          className="w-16 h-16 md:w-20 md:h-20 rounded-xl shadow-lg"
        />
        <h2 className="text-3xl md:text-4xl font-bold">Currents</h2>
        <p className="text-lg">Available for iPhone</p>
        <Button 
          className="mt-4 rounded-full bg-zinc-900 px-8 py-3 text-white hover:bg-zinc-800"
          onClick={() => window.open('https://apps.apple.com/us/app/diverplus-diving-app/id6504115639', '_blank')}
        >
          Download iOS App
        </Button>
      </div>
    </section>
  )
}

export const Footer: React.FC = () => {
  const SocialIcon: React.FC<SocialIconProps> = ({ Icon, href, label }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#1F80FF]">
      <Icon className="w-6 h-6" />
      <span className="sr-only">{label}</span>
    </a>
  )

  return (
    <footer className="py-12 relative z-20">
      <div className="container">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          <div>
            <h3 className="font-semibold mb-2">Currents</h3>
            <ul className="space-y-2">
              <li><a href="/pro" className="text-sm text-muted-foreground hover:text-foreground">Currents PRO</a></li>
              <li><a href="/compatibility" className="text-sm text-muted-foreground hover:text-foreground">Compatibility</a></li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Company</h3>
            <ul className="space-y-2">
              <li><a href="/about" className="text-sm text-muted-foreground hover:text-foreground">About Us</a></li>
              <li><a href="/research" className="text-sm text-muted-foreground hover:text-foreground">Research</a></li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Legal</h3>
            <ul className="space-y-2">
              <li><a href="/terms-of-service" className="text-sm text-muted-foreground hover:text-foreground">Terms of Service</a></li>
              <li><a href="/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground">Privacy Policy</a></li>
            </ul>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">
              The modern dive logging app. <br />Download Currents today.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <SocialIcon Icon={FaInstagram} href="https://www.instagram.com/currentsapp" label="Currents Instagram" />
            <SocialIcon Icon={FaTwitter} href="https://twitter.com/currentsapp" label="Currents Twitter" />
            <SocialIcon Icon={FaTiktok} href="https://www.tiktok.com/@currentsapp" label="Currents TikTok" />
          </div>
        </div>
        <div className=" pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Currents. All rights reserved.</p>
          <div className="flex space-x-4 mt-4 md:mt-0">
            <a href="/terms-of-service" className="hover:text-foreground">Terms of Service</a>
            <a href="/privacy-policy" className="hover:text-foreground">Privacy Policy</a>
          </div>
        </div>
      </div>
    </footer>
  )
}

const LandingPage: React.FC = () => {
  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO path="/" />
      <Header />
      <main className="flex-1">
        <HeroSection />
        <AppShowcase />
        <PartnersSection />
        <FeaturesSection />
        <CtaSection />
      </main>
      <GradientBackground />
      <Footer />
      <Analytics />
    </div>
  )
}

export default LandingPage
